"use client";

import React, {
  startTransition,
  useActionState,
  useEffect,
  useState,
} from "react";
import SectionLayout from "../ui/SectionLayout";
import ZodErrors from "../ui/ZodErrors";
import { submitWishForm } from "@/actions/wish-form";
import { WISH_INITIAL_STATE } from "@/services/form.service";
import ZipCodeInput from "../ui/inputs/ZipCodeInput";
import ConfirmModal from "../ui/ConfirmModal";
import { SectionProps } from "./model";
import { formatPhoneNumberObject, getDefaultCmsSection } from "@/utils/helper";
import { motion } from "motion/react";
import {
  NamespaceCoco,
  WishDefinitionFraction,
  IntervalFraction,
} from "@/utils/gql/graphql";
import RangeSlider from "../ui/sliders/RangeSlider";
import TextAreaInput from "../ui/inputs/TextAreaInput";
import TextInput from "../ui/inputs/TextInput";
import CheckboxInput from "../ui/inputs/CheckboxInput";
import FormLayout from "../ui/inputs/FormLayout";
import { getPaddings } from "@/utils/theme-helper";
import { ZipCode, ZipCodeGroup } from "@/types/zipcode";
import { useReCaptcha } from "next-recaptcha-v3";
import StoreSelect from "../ui/inputs/StoreSelect";
import { withRichTextRuleColours } from "@/utils/rich-text-rule";

export interface OptionItemProps {
  option: {
    selected: boolean;
    id: string;
    text: string;
  };
  setEnabled: (checked: boolean) => void;
}

export interface WishTab {
  id: string;
  label: string;
  config: WishDefinitionFraction & { ranges: any[] };
}

interface IntegerRangeModel {
  lowerValue?: number;
  upperValue?: number;
}

const WishFormSectionComponent: React.FC<
  SectionProps<{
    wishes: {
      wishNamespace: string;
      title: string;
      estateNamespaces: {
        displayName: string;
        namespace: string;
      }[];
      rangeCriterias: {
        displayName: string;
        namespace: string;
      }[];
    }[];
    overskrift: string;
    formPadding: string;
    tekst: string;
    submitLabel: string;
  }>
> = ({ cmsSection, cmsData, app }) => {
  const zipCodeGroups = app.intervals[0].items.map(
    (item: IntervalFraction) => ({
      ...item,
      isGroup: true as const,
    }),
  );

  const wishTabs = cmsSection.specifics.wishes
    .map((item) => {
      const wishDefinition = app.wishDefinitions.find(
        (x: WishDefinitionFraction) => x.namespace === item.wishNamespace,
      );

      if (!wishDefinition) {
        return null;
      }

      const estateNamespaces =
        (item.estateNamespaces ?? []).length > 0
          ? item.estateNamespaces
          : wishDefinition?.estatedNamespaces;

      return {
        id: item.wishNamespace,
        label: item.title,
        config: {
          ...wishDefinition,
          estatedNamespaces: estateNamespaces,
          ranges: wishDefinition.rangeNamespaces
            ?.map((criteria: NamespaceCoco) => {
              return app.ranges.find(
                (y: any) => y.instanceNamespace === criteria.namespace,
              );
            })
            .filter((x: any) => !!x),
        },
      };
    })
    .filter((x) => !!x);

  const [formHash, setFormHash] = useState<string>("");
  const [selectedWishTab, setSelectedWishTab] = useState<WishTab>(wishTabs[0]);
  const [selectedEstateNamespaces, setSelectedEstateNamespaces] = useState<
    string[]
  >([]);
  const [selectedZipCodes, setSelectedZipCodes] = useState<
    (ZipCode | ZipCodeGroup)[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);

  const [formState, formAction, isPending] = useActionState(
    async (prevState: any, formData: FormData) => {
      if (selectedEstateNamespaces.length === 0) {
        return {
          ...prevState,
          zodErrors: {
            estateNamespaces: ["Der skal foretages minimum et valg"],
          },
          isSuccess: false,
        };
      }

      // Check for zipcode selection
      if (selectedZipCodes.length === 0) {
        return {
          ...prevState,
          zodErrors: {
            zipcodes: [
              "Vælg venligst mindst ét postnummer eller postnummerinterval",
            ],
          },
          isSuccess: false,
        };
      }

      try {
        const zipCodeRanges: IntegerRangeModel[] = selectedZipCodes
          .filter((item): item is ZipCodeGroup => item.isGroup)
          .map((group) => ({
            lowerValue: group.from,
            upperValue: group.to,
          }));
        console.log(zipCodeRanges);
        const individualZipCodes = selectedZipCodes
          .filter((item): item is ZipCode => !item.isGroup)
          .map((zip) => zip.nr);

        Object.entries(formData).forEach(([key, value]) => {
          if (key.includes("Range")) {
            formData.set(key, value.toString());
          }
        });

        formData.append("zipCodeRanges", JSON.stringify(zipCodeRanges));
        formData.append("zipcodes", individualZipCodes.join(","));
        formData.append("estateNamespaces", selectedEstateNamespaces.join(","));
        formData.append("wishNamespace", selectedWishTab.config!.namespace!);

        return submitWishForm(prevState, formData).then((response) => {
          if (response.isSuccess) {
            setSelectedEstateNamespaces([]);
            setSelectedZipCodes([]);
            setSelectedWishTab({ ...selectedWishTab });
            setFormHash(Math.random().toString(36).substring(2, 15));

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "wishform_submitted",
            });
            window?.pageVisitTracker?.track("WishFormSubmit", {});
          }

          return response;
        });
      } catch (error) {
        console.error("Form submission error:", error);
        return {
          ...prevState,
          apiErrors: "Der opstod en fejl ved indsendelse af formularen",
          isSuccess: false,
        };
      }
    },
    WISH_INITIAL_STATE,
  );

  const { executeRecaptcha } = useReCaptcha();
  const submit = (payload: FormData) => {
    (async () => {
      try {
        const token = await executeRecaptcha("WishForm");
        payload.append("recaptchaToken", token);
      } catch (error) {
        console.log("[ReCaptcha Error]:", error);
        payload.delete("recaptchaToken");
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  };

  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    console.log("Recaptcha:", {
      enabled: formState.recaptchaIsEnabled,
      validToken: formState.recaptchaIsValid,
    });

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true);
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);

  useEffect(() => {
    setSelectedEstateNamespaces([]);
    setFormHash(Math.random().toString(36).substring(2, 15));
  }, [selectedWishTab]);

  const {
    firstName,
    lastName,
    address,
    zip,
    city,
    phone,
    email,
    internalNote,
  } = formState?.data || {};
  return (
    <SectionLayout cmsSection={getDefaultCmsSection(cmsSection)}>
      <ConfirmModal
        description="Du kan altid kontakte os på:"
        title="Du er nu tilmeldt køberkartoteket."
        setOpen={(isOpen) => {
          setIsOpen(isOpen);
        }}
        isOpen={isOpen}
        contactInfo={{
          phone: formatPhoneNumberObject(formState.contactInfo?.phone ?? ""),
          email: formState.contactInfo?.email?.address ?? "",
        }}
      />
      <ConfirmModal
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />

      <div className="flex flex-col homepage-text-sm">
        <div className="flex flex-col md:flex-row gap-0 md:gap-4">
          <div className="flex md:w-1/3 flex-col gap-4">
            <div className="homepage-text-lg">
              {cmsSection.specifics.overskrift}
            </div>
            <div className="flex p-4 md:p-0">
              {cmsSection.specifics.tekst && (
                <div
                  className="styled-description"
                  dangerouslySetInnerHTML={{
                    __html: withRichTextRuleColours(
                      cmsSection.specifics.tekst ?? "",
                    ),
                  }}
                ></div>
              )}
            </div>
          </div>
          <div className="flex flex-col flex-1 lg-w-2/3">
            {wishTabs.length > 1 && (
              <nav className="bg-[var(--tabs-bg-color)]">
                <ul className="flex gap-4 overflow-auto">
                  {wishTabs.map((item, index) => {
                    const isSelected = selectedWishTab.id === item.id;
                    return (
                      <li
                        key={index}
                        role="tab"
                        className="relative"
                        aria-selected={isSelected}
                      >
                        {isSelected ? (
                          <motion.div
                            layoutId="selected"
                            className="absolute bg-[var(--tabs-active-bg-color)] inset-0 z-1"
                          />
                        ) : null}
                        <motion.button
                          data-active={isSelected}
                          className="z-2 homepage-text-sm relative cursor-pointer p-4 data-[active=false]:text-[var(--tabs-text-color)] data-[active=true]:text-[var(--tabs-active-text-color)]"
                          onTapStart={() =>
                            setSelectedWishTab(
                              wishTabs.find((x) => x.id === item.id)!,
                            )
                          }
                          whileTap={{ scale: 0.9 }}
                          whileFocus={{
                            backgroundColor: "var(--accent-transparent)",
                          }}
                        >
                          {item?.label}
                        </motion.button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            )}
            <div
              className={`bg-[var(--tabs-active-bg-color)] ${getPaddings(cmsSection?.specifics?.formPadding)}`}
            >
              <FormLayout
                submitLabel={cmsSection.specifics.submitLabel ?? "Send"}
                featureFlags={cmsData?.featureFlags}
                isPending={isPending}
                formAction={submit}
              >
                <StoreSelect
                  cmsData={cmsData}
                  label="Vælg butik"
                  name="resourceGroupId"
                  stores={app.stores}
                />
                <ZipCodeInput
                  cmsData={cmsData}
                  errors={formState?.zodErrors?.zipcodes}
                  onChange={(item) => {
                    setSelectedZipCodes(item);
                  }}
                  selectedZipCodes={selectedZipCodes}
                  zipCodeGroups={zipCodeGroups}
                >
                  <ZodErrors errors={formState?.zodErrors?.zipcodes} />
                </ZipCodeInput>
                <div
                  className={`grid grid-cols-2 col-span-6 md:grid-cols-3 group ${cmsData.forms.style?.type?.startsWith("material") ? "" : "gap-4"}`}
                >
                  {selectedWishTab.config?.estatedNamespaces?.map((x: any) => (
                    <CheckboxInput
                      cmsData={cmsData}
                      key={x.namespace}
                      label={x.displayName}
                      isChecked={selectedEstateNamespaces.includes(x.namespace)}
                      onChanged={(checked) => {
                        if (checked) {
                          setSelectedEstateNamespaces([
                            ...selectedEstateNamespaces,
                            x.namespace,
                          ]);
                        } else {
                          setSelectedEstateNamespaces(
                            selectedEstateNamespaces.filter(
                              (item) => item !== x.namespace,
                            ),
                          );
                        }
                      }}
                    />
                  ))}
                  <ZodErrors errors={formState?.zodErrors?.estateNamespaces} />
                </div>
                {selectedWishTab.config?.ranges?.map((x) => (
                  <RangeSlider
                    key={`${x.instanceNamespace}-${formHash}`}
                    range={x}
                  />
                ))}
                <TextAreaInput
                  cmsData={cmsData}
                  name="internalNote"
                  label="Øvrige ønsker til ejendommen"
                  defaultValue={internalNote}
                  errors={formState?.zodErrors?.internalNote}
                  required={false}
                />
                <TextInput
                  cmsData={cmsData}
                  name="firstName"
                  label="Fornavn"
                  defaultValue={firstName}
                  errors={formState?.zodErrors?.firstName}
                  required={cmsData?.forms.nameRequired}
                />
                <TextInput
                  cmsData={cmsData}
                  name="lastName"
                  label="Efternavn"
                  defaultValue={lastName}
                  errors={formState?.zodErrors?.lastName}
                  required={cmsData?.forms.nameRequired}
                />
                <TextInput
                  cmsData={cmsData}
                  className="col-span-6"
                  name="address"
                  label="Adresse"
                  defaultValue={address}
                  errors={formState?.zodErrors?.address}
                  required={cmsData?.forms.addressRequired}
                />
                <TextInput
                  cmsData={cmsData}
                  className="col-span-2"
                  name="zip"
                  label="Postnr"
                  defaultValue={zip}
                  errors={formState?.zodErrors?.zip}
                  required={cmsData?.forms.zipRequired}
                />
                <TextInput
                  cmsData={cmsData}
                  className="col-span-4"
                  name="city"
                  label="By"
                  defaultValue={city}
                  errors={formState?.zodErrors?.city}
                  required={cmsData?.forms.cityRequired}
                />
                <TextInput
                  cmsData={cmsData}
                  className="col-span-3"
                  name="phone"
                  label="Telefonnummer"
                  defaultValue={phone}
                  errors={formState?.zodErrors?.phone}
                  required={cmsData?.forms.phoneRequired}
                />
                <TextInput
                  cmsData={cmsData}
                  className="col-span-3"
                  name="email"
                  label="E-mail"
                  defaultValue={email}
                  errors={formState?.zodErrors?.email}
                  required={cmsData?.forms.emailRequired}
                />
              </FormLayout>
            </div>
          </div>
        </div>
      </div>
    </SectionLayout>
  );
};

export default WishFormSectionComponent;
